'use client';

import Breadcrumbs from '@/components/Breadcrumbs';
import WebOnly from '@/components/Responsive/WapOnly';
import { useMediaQuery } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTrack } from '../../hooks/useTrack';

export interface BannerData {
  type: number; // 1: 图片, 2: 视频
  title?: string;
  title_m?: string;
  description?: string;
  description_m?: string;
  image_pc?: string;
  image_m?: string;
  aspect_ratio_pc?: string;
  aspect_ratio_m?: string;
  word_color?: string;
  word_color_m?: string;
  background_color?: string;
  group?: string | number;
}

interface BannerProps {
  bannerData: BannerData;
  ts?: number;
}

export default function Banner({ bannerData, ts = 1 }: BannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { trackBannerExp } = useTrack();
  const router = useRouter();
  const isMobileScreen = useMediaQuery('(max-width: 768px)');
  const [bannerRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (video && bannerData.type === 2) {
      const playVideo = async () => {
        await video.play();
      };

      playVideo();
    }
  }, [bannerData.type]);

  useEffect(() => {
    if (inView) {
      trackBannerExp(bannerData.group);
    }
  }, [inView, trackBannerExp, bannerData.group]);

  const currentTitle = isMobileScreen ? bannerData.title_m || bannerData.title : bannerData.title;
  const currentDescription = isMobileScreen
    ? bannerData.description_m || bannerData.description
    : bannerData.description;
  const currentImage = isMobileScreen ? bannerData.image_m : bannerData.image_pc;
  const currentAspectRatio = isMobileScreen
    ? bannerData.aspect_ratio_m
    : bannerData.aspect_ratio_pc;
  const currentWordColor = isMobileScreen
    ? bannerData.word_color_m || bannerData.word_color
    : bannerData.word_color;

  return (
    <div
      ref={bannerRef}
      className="relative flex items-center justify-center md:items-end md:justify-end"
      data-ts={ts}
      style={{
        aspectRatio: currentAspectRatio || (isMobileScreen ? '750/780' : '1920/480'),
        backgroundColor: bannerData.background_color || '#ffffff',
      }}
    >
      {bannerData.type === 1 ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={currentImage || ''} alt={currentTitle || ''} className="block h-full w-auto" />
      ) : (
        <div className="w-full h-auto">
          <video
            ref={videoRef}
            className="block w-full h-auto"
            loop
            autoPlay
            muted
            preload="auto"
            playsInline
          >
            <source src={currentImage} />
          </video>
        </div>
      )}

      <div
        className="absolute bottom-[10vw] left-[2.5vw] box-border flex w-[90%] max-w-[90%] flex-col items-center justify-center text-left md:top-0 md:bottom-auto md:h-full md:items-start md:justify-center"
        style={{ color: currentWordColor }}
      >
        <h1 className="font-bb text-2xl md:mb-4 md:text-[clamp(32px,4vw,56px)]">{currentTitle}</h1>
        <h5 className="leading-20 text-center text-[13px] md:text-left md:text-[clamp(16px,1.5vw,20px)]">
          {currentDescription}
        </h5>
      </div>

      <WebOnly>
        <Breadcrumbs
          items={[
            {
              label: 'Home',
              href: '/',
              onClick: () => {
                router.push('/');
              },
            },
            { label: currentTitle },
          ]}
          className="absolute top-[10px] left-[15px] z-10"
        />
      </WebOnly>
    </div>
  );
}
